import { inject, injectable } from "tsyringe";
import { Auth0Client, createAuth0Client, User, GenericError } from '@auth0/auth0-spa-js';
import log from "loglevel";
import { TOKEN_IProjectBus, type IProjectBus } from "./ProjectBus";
import { generateShortClientIdentity, urlFriendlyBase64 } from "@utilitydelta/nosignup-js";

export const TOKEN_IWrapAuth0 = Symbol('IWrapAuth0');

export interface AuthConfig {
    domain: string;
    clientId: string;
    organization?: string;
    audience?: string;
}

export interface AuthState {
    isAuthenticated: boolean;
    user?: User;
    token?: string;
    isLoading: boolean;
    error?: string;
}

export interface IWrapAuth0 {
    /**
     * Gets the current authentication state
     */
    get state(): AuthState;

    get userIdHashed(): string | undefined;

    get userIdHashedUrlFriendly(): string | undefined;

    /**
     * Initializes the Auth0 client and attempts silent authentication
     */
    initialize(): Promise<void>;

    /**
     * Logs in the user using redirect
     */
    loginWithRedirect(options?: { organization?: string; invitation?: string }): Promise<void>;

    /**
     * Logs in the user using popup
     */
    loginWithPopup(options?: { organization?: string; invitation?: string }): Promise<void>;

    /**
     * Handles the redirect callback after login
     */
    handleRedirectCallback(): Promise<void>;

    /**
     * Gets the access token silently
     */
    getTokenSilently(): Promise<string | undefined>;

    /**
     * Gets the current user
     */
    getUser(): Promise<User | undefined>;

    /**
     * Logs out the user
     */
    logout(options?: { returnTo?: string }): Promise<void>;

    /**
     * Checks if the user is authenticated
     */
    isAuthenticated(): Promise<boolean>;
}

@injectable()
export class WrapAuth0 implements IWrapAuth0 {
    private auth0Client?: Auth0Client;
    private _state: AuthState = {
        isAuthenticated: false,
        isLoading: true
    };
    private _userIdHashed?: string | undefined;
    private _userIdHashedUrlFriendly?: string | undefined;

    public get userIdHashedUrlFriendly(): string | undefined {
        return this._userIdHashedUrlFriendly;
    }

    public get userIdHashed(): string | undefined {
        return this._userIdHashed;
    }

    constructor(
        @inject(TOKEN_IProjectBus) private projectBus: IProjectBus
    ) {
        log.info("Creating WrapAuth0");
    }

    public get state(): AuthState {
        return { ...this._state };
    }

    public async initialize(): Promise<void> {
        try {
            await this.setState({ isLoading: true, error: undefined });

            const config = await this.fetchAuthConfig();

            this.auth0Client = await createAuth0Client({
                domain: config.domain,
                clientId: config.clientId,
                cacheLocation: 'localstorage',
                useRefreshTokens: true,
                authorizationParams: {
                    redirect_uri: window.location.origin,
                    organization: config.organization,
                    audience: config.audience,
                    scope: 'openid profile email'
                }
            });

            // Check if we're returning from a login redirect
            if (window.location.search.includes('code=') || window.location.search.includes('error=')) {
                await this.handleRedirectCallback();
                return;
            }

            // Try to get token silently
            try {
                const token = await this.auth0Client.getTokenSilently();
                const user = await this.auth0Client.getUser();

                await this.setState({
                    isAuthenticated: true,
                    user,
                    token,
                    isLoading: false
                });

                this.projectBus.authenticationChanged({ isAuthenticated: true, user });
            } catch (error) {
                const authError = error as GenericError;
                if (authError.error === 'login_required') {
                    await this.setState({
                        isAuthenticated: false,
                        isLoading: false
                    });
                    this.projectBus.authenticationChanged({ isAuthenticated: false });
                } else {
                    log.error("Silent authentication failed:", authError);
                    await this.setState({
                        isAuthenticated: false,
                        isLoading: false,
                        error: authError.message
                    });
                    this.projectBus.authenticationChanged({ isAuthenticated: false, error: authError.message });
                }
            }
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Auth0 initialization failed';
            log.error("Auth0 initialization failed:", error);
            await this.setState({
                isAuthenticated: false,
                isLoading: false,
                error: errorMessage
            });
            this.projectBus.authenticationChanged({ isAuthenticated: false, error: errorMessage });
        }
    }

    public async loginWithRedirect(options?: { organization?: string; invitation?: string }): Promise<void> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        try {
            await this.setState({ isLoading: true, error: undefined });

            const authorizationParams: any = {
                redirect_uri: window.location.origin
            };

            if (options?.organization) {
                authorizationParams.organization = options.organization;
            }

            if (options?.invitation) {
                authorizationParams.invitation = options.invitation;
            }

            await this.auth0Client.loginWithRedirect({
                authorizationParams
            });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login redirect failed';
            log.error("Login redirect failed:", error);
            await this.setState({
                isLoading: false,
                error: errorMessage
            });
            throw error;
        }
    }

    public async loginWithPopup(options?: { organization?: string; invitation?: string }): Promise<void> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        try {
            await this.setState({ isLoading: true, error: undefined });

            const authorizationParams: any = {};

            if (options?.organization) {
                authorizationParams.organization = options.organization;
            }

            if (options?.invitation) {
                authorizationParams.invitation = options.invitation;
            }

            await this.auth0Client.loginWithPopup({
                authorizationParams
            });

            const token = await this.auth0Client.getTokenSilently();
            const user = await this.auth0Client.getUser();

            await this.setState({
                isAuthenticated: true,
                user,
                token,
                isLoading: false
            });

            this.projectBus.authenticationChanged({ isAuthenticated: true, user });
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Login popup failed';
            log.error("Login popup failed:", error);
            await this.setState({
                isLoading: false,
                error: errorMessage
            });
            throw error;
        }
    }

    public async handleRedirectCallback(): Promise<void> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        try {
            await this.setState({ isLoading: true, error: undefined });

            await this.auth0Client.handleRedirectCallback();

            const token = await this.auth0Client.getTokenSilently();
            const user = await this.auth0Client.getUser();

            await this.setState({
                isAuthenticated: true,
                user,
                token,
                isLoading: false
            });

            this.projectBus.authenticationChanged({ isAuthenticated: true, user });

            // Clean up the URL
            window.history.replaceState({}, document.title, window.location.pathname);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Redirect callback failed';
            log.error("Redirect callback failed:", error);
            await this.setState({
                isAuthenticated: false,
                isLoading: false,
                error: errorMessage
            });
            this.projectBus.authenticationChanged({ isAuthenticated: false, error: errorMessage });
        }
    }

    public async getTokenSilently(): Promise<string | undefined> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        try {
            const token = await this.auth0Client.getTokenSilently();

            this._state.token = token;

            return token;
        } catch (error) {
            return undefined;
        }
    }

    public async getUser(): Promise<User | undefined> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        return await this.auth0Client.getUser();
    }

    public async logout(options?: { returnTo?: string }): Promise<void> {
        if (!this.auth0Client) {
            throw new Error('Auth0 client not initialized');
        }

        try {
            await this.setState({
                isAuthenticated: false,
                user: undefined,
                token: undefined,
                isLoading: false,
                error: undefined
            });

            this.projectBus.authenticationChanged({ isAuthenticated: false });

            await this.auth0Client.logout({
                logoutParams: {
                    returnTo: options?.returnTo || window.location.origin
                }
            });
        } catch (error) {
            log.error("Logout failed:", error);
            throw error;
        }
    }

    public async isAuthenticated(): Promise<boolean> {
        if (!this.auth0Client) {
            return false;
        }

        return await this.auth0Client.isAuthenticated();
    }

    private async fetchAuthConfig(): Promise<AuthConfig> {
        try {
            const response = await fetch("/auth_config.json");
            const config = await response.json();

            return {
                domain: config.domain,
                clientId: config.clientId,
                organization: config.organization,
                audience: config.audience
            };
        } catch (error) {
            log.error("Failed to fetch auth config:", error);
            throw new Error('Failed to load authentication configuration');
        }
    }

    private async setState(newState: Partial<AuthState>): Promise<void> {
        this._state = { ...this._state, ...newState };
        const sub = this._state?.user?.sub;
        if (sub !== undefined) {
            this._userIdHashed = await generateShortClientIdentity(sub);
            this._userIdHashedUrlFriendly = urlFriendlyBase64(this._userIdHashed);
        } else {
            this._userIdHashed = undefined;
            this._userIdHashedUrlFriendly = undefined;
        }
        log.debug("Auth state updated:", this._state);
    }
}